// 測定セッションの wire 型（§14）。FarmSet とは独立ドメイン・独立スキーマ版。
// 累計スタイル: 総タイマー＋周回数＋ドロップまとめカウンタ（周回ごとのラップログは持たない）。
import type { CurrencyId } from './schema';

export const MEASURE_SCHEMA_VERSION = 1;

/**
 * ドロップカウンタの出自。取り込み時にセットのどこへ加算するかの対応付けキー。
 * - set-line: リンクセットの DropLine 由来（lineId = DropLine.id）
 * - set-member: GroupDrop メンバー由来（グループは取り込み時に全メンバー一括）
 * - adhoc: セッション中に追加（取り込み時はセットへ新規 empirical 行を作成）
 */
export type MeasureDropSource =
  | { kind: 'set-line'; lineId: string }
  | { kind: 'set-member'; groupId: string; memberId: string }
  | { kind: 'adhoc' };

/**
 * ドロップまとめカウンタ。itemId の有無は ValueRow と同じ2概念（自動相場/手動）。
 *
 * 取り込みウォーターマークはカウンタごとに (importedRuns, importedCount) のペアで持つ。
 * 差分 ΔN = session.runs - importedRuns / Δk = count - importedCount が常に対で取り込まれる
 * ため、行ごとの選択取り込みやセッション途中で追加したカウンタ（観測窓が途中から始まる）
 * でも N と k の対応が壊れない。作成時は importedRuns = その時点の session.runs。
 */
export interface MeasureDropCounter {
  id: string;
  name: string;
  itemId?: string;
  unit: CurrencyId;
  /** 手動時の1個あたり価値（unit 建て）。自動は rate(itemId) を使う。 */
  value: number;
  /** セッション累計の観測個数 k。 */
  count: number;
  /** 取り込み済み観測個数（ウォーターマーク）。Δk = count - importedCount。 */
  importedCount: number;
  /** 取り込み済み周回数（ウォーターマーク兼観測窓の開始）。ΔN = session.runs - importedRuns。 */
  importedRuns: number;
  source: MeasureDropSource;
}

/** 臨時コスト（想定外の消費・セッション総額）。周回比例のセットコストとは別勘定。 */
export interface MeasureExtraCost {
  id: string;
  name: string;
  itemId?: string;
  unit: CurrencyId;
  value: number;
  /** 消費総数（周回比例ではない）。 */
  count: number;
}

/**
 * 測定セッション。リーグごとに保持（§8 の保存規約に倣う）。
 *
 * タイマー会計は Date.now ベース:
 * - 実効経過 = elapsedMs + (now - runningSince)（走行中のみ後者を加算）
 * - 一時停止で elapsedMs に畳んで runningSince=null
 * - 走行中は数秒ごとに lastTickAt を heartbeat 保存。リロード復元時は
 *   (lastTickAt - runningSince) だけを畳んで一時停止状態で復元し、
 *   ブラウザを閉じていた時間を計上しない。
 */
export interface MeasureSession {
  schemaVersion: number;
  id: string;
  name: string;
  /** 所属リーグ（保存の分割キー）。 */
  league: string;
  /** リンク先ファームセット（同一リーグ内・任意）。 */
  linkedSetId?: string;
  elapsedMs: number;
  runningSince: number | null;
  lastTickAt: number | null;
  /** 周回数 N。 */
  runs: number;
  drops: MeasureDropCounter[];
  extraCosts: MeasureExtraCost[];
  createdAt: number;
  updatedAt: number;
}
